import { groupBy, isEmpty, isNil, uniqBy } from 'lodash';
import './MissionUtils.scss';
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as ChevronLeft } from 'assets/icons/chevron-left.svg';
import { ReactComponent as ExportIcon } from 'assets/icons/export-icon.svg';
import { getClientMissions } from 'services/Rest';

/** DATA MAPPING **/
import {
  FUNDING_METHODS,
  handleSSJNav,
  MAINTENANCE_METHODS
} from 'services/OnBoardingJourney';
import { sortByKey } from 'services/Sorts';
import FundingMethodSelection from '../../../layouts/FundingMethodSelections/FundingMethodSelection';
import BankInstructions from '../instructions/BankInstructions';
import ExchangeInstructions from '../instructions/ExchangeInstructions';
import RolloverInstructions from '../instructions/RolloverInstructions';
import TransferInstructions from '../instructions/TransferInstructions';
import MaintenanceOptOutErrorInstructions from '../instructions/MaintenanceOptOurErrorInstructions';
import BrokerageTradingInstructions from '../instructions/BrokerageTradingInstructions';
import { optOutOfClientMissions } from 'services/Rest';
import { isBrokerage } from 'services/Products';
import { openBrokerageAuthTab } from 'services/ExternalAuthorizationURLBuilder';
import BeneficiaryInstructions from '../instructions/BeneficiaryInstructions';
import AutoBuyInstructions from '../instructions/AutoBuyInstructions';
import TrustedContactInstructions from '../instructions/TrustedContactInstructions';
import React from 'react';
import { closeStepModal } from '../../StepModal/StepModal';
import { dispatchOverlayLoad } from 'state/logic/AnalyticsLogic';
import { isTaxableAccount } from 'services/AccountTypes';

export const MISSION_STATUS = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  PENDING: 'PENDING',
  OPT_OUT: 'OPT_OUT',
  EXPIRE: 'EXPIRE'
};

export const ONBOARDING_MISSIONS = {
  FUNDING: 'FUNDING',
  MAINTENANCE: 'MAINTENANCE'
};

export const HEADER_CONTENT = {
  ONBOARDING: 'Setup',
  FUNDING: 'Fund your'
};
export const mapProgressStep = (submissionStep, i) => {
  return {
    ...submissionStep,
    id: i,
    label: submissionStep.stepName,
    status: submissionStep.stepStatus
  };
};

/** REUSABLE FILTERS **/

export const filterVisibleMissions = (mission) => {
  return mission.status !== 'OPTED_OUT';
};

export const filterCompletedMissions = (missionStep) => {
  return isMissionComplete(missionStep.missionStatus);
};

/** LOGIC HELPERS **/
export const expandSubSteps = (status, action, fundingType) => {
  return (
    (isMissionComplete(status) || isMissionActive(status)) &&
    action === 'FUNDING' &&
    fundingType &&
    fundingType !== FUNDING_METHODS.NONE
  );
};

export const isMissionActive = (status) => {
  return (
    status === MISSION_STATUS.PENDING || status === MISSION_STATUS.IN_PROGRESS
  );
};

export const isMissionComplete = (status) => {
  return status === MISSION_STATUS.COMPLETED || status === 'COMPLETE';
};

export const getOverallMissionProgress = (missionSteps) => {
  let progress = 0;
  progress =
    missionSteps.filter(filterCompletedMissions).length / missionSteps.length;
  return progress;
};

export const sortMissionsByCreationDate = (missions) => {
  return missions.sort((a, b) =>
    sortByKey(a, b, 'onboardingCreatedDate', 'asc')
  );
};

export const getCurrentMissionName = (missions) => {
  const {
    onboardingFundingMission: { status: fundingStatus } = {},
    onboardingMaintenanceMission: { status: maintenanceStatus } = {}
  } = missions;

  if (
    fundingStatus &&
    isMissionComplete(fundingStatus) &&
    maintenanceStatus &&
    isMissionActive(maintenanceStatus)
  ) {
    return ONBOARDING_MISSIONS.MAINTENANCE;
  }

  return ONBOARDING_MISSIONS.FUNDING;
};

export const getCurrentMission = (missions) => {
  const { onboardingFundingMission, onboardingMaintenanceMission } = missions;
  const missionName = getCurrentMissionName(missions);

  return missionName === ONBOARDING_MISSIONS.FUNDING
    ? onboardingFundingMission
    : onboardingMaintenanceMission;
};

export const maintenanceMissionsSortOrder = [
  MAINTENANCE_METHODS.BROKERAGE_TRADING,
  MAINTENANCE_METHODS.RECURRING_BUY,
  MAINTENANCE_METHODS.TRUSTED_CONTACT,
  MAINTENANCE_METHODS.BENEFICIARY
];

export const sortMaintenanceMissions = (missions) => {
  if (missions && missions.length) {
    return missions.sort(
      (a, b) =>
        maintenanceMissionsSortOrder.indexOf(a.action) -
        maintenanceMissionsSortOrder.indexOf(b.action)
    );
  }
};

//Today onboarding only is for Mutual Fund, Brokerage w/Sweep, revisit this util if other products are added.
export const getVisibleMutualFundMaintenanceMissions = missions => {
  if(missions && missions.length) {
    return missions.filter(mission => isMissionComplete(mission.onboardingFundingMission.status) && isMissionActive(mission.onboardingMaintenanceMission.status) && !isBrokerage(mission.agreementKey.productType.enumerationID));
  }
};

export const getVisibleBrokerageMaintenanceMissions = missions => {
  if(missions && missions.length) {
    return missions.filter(mission => isMissionComplete(mission.onboardingFundingMission.status) && isMissionActive(mission.onboardingMaintenanceMission.status) && isBrokerage(mission.agreementKey.productType.enumerationID));
  }
};

export const rollupMissionsByRegistration = (missions) => {
  if (missions && missions.length) {
    return groupBy(missions, (mission) => mission.registrationKey);
  }

  return {};
};

export const getVisibleMaintenanceMissionsFromRollup = (group) => {
  let pendingMaintenanceMissions = [];
  group.forEach((mission) => {
    const { onboardingMaintenanceMission: onboardingMissions } = mission;
    if (
      !isEmpty(onboardingMissions) &&
      isMissionActive(onboardingMissions.status)
    ) {
      const pendingMissions =
        mission.onboardingMaintenanceMission.onboardingMissions.filter((step) =>
          isMissionActive(step.missionStatus)
        );
      if (pendingMissions.length) {
        pendingMaintenanceMissions.push(...pendingMissions);
      }
    }
  });

  return pendingMaintenanceMissions;
};

export const getBrokerageMaintenanceMissionsBySweepId = (brokerageMissions, maintenanceMissions) => {
  const missions = [];

  maintenanceMissions.forEach(m => {
    //We want to get the maintenance missions for the brokerage agreement that is linked to the sweep account.
    const linkedBrkMission = brokerageMissions.find(b => b.sweepAgreementKeys.find(s => s.agreementID === m.agreementKey.agreementID));
    if(linkedBrkMission) {
      linkedBrkMission.onboardingMaintenanceMission.onboardingMissions.filter(f => isMissionActive(f.missionStatus)).forEach(brk => missions.push(brk));
    }
  });

  return missions;
};

export const getPendingBrokerageMissionsWithNoActiveSweepMissions = (brokerageMissions, mfMissions) => {
  const pendingMissions = [];

  //Since brokerage and MF sweeps do not have matching registration keys, we have to check if there are any active missions on the sweep account.
  //If there are active missions on the sweep we will not do anything here because the logic for MF accounts will include the pending brokerage missions
  //If there are no active missions on the sweep, we will return a single maintenance mission with BRK agreements rolled up by registration.
  brokerageMissions.forEach(brkMission => {
    const hasActiveSweepMission = brkMission.sweepAgreementKeys.some(sweep => mfMissions.find(mfMission => mfMission.agreementKey.agreementID === sweep.agreementID));
    if(!hasActiveSweepMission) {
      brkMission.onboardingMaintenanceMission.onboardingMissions.filter(brkMaintenanceMission => isMissionActive(brkMaintenanceMission.missionStatus)).forEach(mission => pendingMissions.push(mission));
    }
  })

  return pendingMissions;
};

export const formatMaintenanceMission = (
  templateMission,
  pendingMaintenanceMissions
) => {
  return {
    ...templateMission,
    onboardingMaintenanceMission: {
      ...templateMission.onboardingMaintenanceMission,
      onboardingMissions: sortMaintenanceMissions(
        uniqBy(pendingMaintenanceMissions, (m) => m.action)
      )
    }
  };
};

export const getTrackerByLikeRegistration = (account, accounts) => {
  if (!account) {
    return [];
  }

  let agreements = [];
  const activeBrokerageMaintenanceMissions = getVisibleBrokerageMaintenanceMissions(accounts);
  const accountsWithLikeRegistration = accounts.filter((a) => a.registrationKey === account.registrationKey);
  const brokerageMaintenanceMissionsBySweepId = activeBrokerageMaintenanceMissions.filter(a => {
    return accountsWithLikeRegistration.find(b => a.sweepAgreementKeys.find(s => s.agreementID === b.agreementKey.agreementID));
  })

  if(accountsWithLikeRegistration && accountsWithLikeRegistration.length) {
    accountsWithLikeRegistration.forEach(m => agreements.push(m));
  }

  if(brokerageMaintenanceMissionsBySweepId && brokerageMaintenanceMissionsBySweepId.length) {
    brokerageMaintenanceMissionsBySweepId.forEach(a => agreements.push(a));
  }

  return agreements;
};

export const getAgreementsForMaintenanceOptOut = (missions, optOutAction) => {
  return missions
    .filter((mission) => {
      const { onboardingMaintenanceMission } = mission;
      const optOutMission =
        onboardingMaintenanceMission.onboardingMissions.find(
          (m) => m.action === optOutAction
        );
      return optOutMission && isMissionActive(optOutMission.missionStatus);
    })
    .map((m) => m.agreementKey);
};

export const buildComponentMap = (
  account,
  history,
  onboardingMaintenanceOptOut,
  fundingMethod,
  webViewEnabled,
  rolledUpMissions,
  requestStatus
) => {
  const taxableSweep = isTaxableAccount(account?.accountTypeId);
  return {
    DEFAULT: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          label: 'Cancel',
          handlePrevious: () => {},
          icon: null
        },
        next: {
          label: 'Continue',
          handleNext: () => {},
          variant: 'primary',
          icon: <ChevronRight slot="suffix" className="chevron-white" />
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    [FUNDING_METHODS.ACH]: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          label: 'Cancel',
          handlePrevious: () => {},
          icon: null
        },
        next: {
          label: 'Continue',
          handleNext: () => {
            dispatchOverlayLoad("Buy shares with a bank account");
          },
          variant: 'primary',
          icon: <ChevronRight slot="suffix" className="chevron-white" />
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      },
      {
        component: <BankInstructions account={account} />,
        previous: {
          label: 'Back',
          handlePrevious: () => {
            dispatchOverlayLoad(taxableSweep ? "Choose your sweep account funding method – Taxable" : "Choose your sweep account funding method – Nontaxable");
          },
          icon: <ChevronLeft slot="prefix" className="chevron-blue" />
        },
        next: {
          label: 'Buy Shares',
          handleNext: () => {
            handleSSJNav(FUNDING_METHODS.ACH, account);
          },
          variant: 'primary',
          icon: null
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    [FUNDING_METHODS.CHECK]: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          label: 'Cancel',
          handlePrevious: () => {},
          icon: null
        },
        next: {
          label: 'Continue',
          handleNext: () => {
            handleSSJNav(FUNDING_METHODS.CHECK, account);
          },
          variant: 'primary',
          icon: <ChevronRight slot="suffix" className="chevron-white" />
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    [FUNDING_METHODS.EXCHANGE]: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          label: 'Cancel',
          handlePrevious: () => {},
          icon: null
        },
        next: {
          label: 'Continue',
          handleNext: () => {
            dispatchOverlayLoad("Exchange existing T. Rowe Price investments")
          },
          variant: 'primary',
          icon: <ChevronRight slot="suffix" className="chevron-white" />
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      },
      {
        component: <ExchangeInstructions account={account} />,
        previous: {
          label: 'Back',
          handlePrevious: () => {
            dispatchOverlayLoad(taxableSweep ? "Choose your sweep account funding method – Taxable" : "Choose your sweep account funding method – Nontaxable");
          },
          icon: <ChevronLeft slot="prefix" className="chevron-blue" />
        },
        next: {
          label: 'Exchange Shares',
          handleNext: () => {
            handleSSJNav(FUNDING_METHODS.EXCHANGE, account);
          },
          icon: null,
          variant: 'primary'
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    [FUNDING_METHODS.INDIRECT_ROLLOVER]: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          icon: null,
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          icon: <ChevronRight slot="suffix" className="chevron-white" />,
          label: 'Continue',
          handleNext: () => {
            dispatchOverlayLoad("Fund with a indirect rollover")
          },
          variant: 'primary'
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      },
      {
        component: <RolloverInstructions account={account} />,
        previous: {
          label: 'Back',
          handlePrevious: () => {
            dispatchOverlayLoad(taxableSweep ? "Choose your sweep account funding method – Taxable" : "Choose your sweep account funding method – Nontaxable");
          },
          icon: <ChevronLeft slot="prefix" className="chevron-blue" />
        },
        next: {
          icon: null,
          label: 'Start My Rollover',
          handleNext: () => {
            handleSSJNav(FUNDING_METHODS.DIRECT_ROLLOVER, account);
          },
          variant: 'primary'
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    [FUNDING_METHODS.DIRECT_ROLLOVER]: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          icon: null,
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          icon: <ChevronRight slot="suffix" className="chevron-white" />,
          label: 'Continue',
          handleNext: () => {
            dispatchOverlayLoad("Fund with a direct rollover")
          },
          variant: 'primary'
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      },
      {
        component: <RolloverInstructions account={account} />,
        previous: {
          icon: <ChevronLeft slot="prefix" className="chevron-blue" />,
          label: 'Back',
          handlePrevious: () => {
            dispatchOverlayLoad(taxableSweep ? "Choose your sweep account funding method – Taxable" : "Choose your sweep account funding method – Nontaxable");
          }
        },
        next: {
          label: 'Start My Rollover',
          handleNext: () => {
            handleSSJNav(FUNDING_METHODS.DIRECT_ROLLOVER, account);
          },
          icon: null,
          variant: 'primary'
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    [FUNDING_METHODS.TRANSFER]: [
      {
        component: <FundingMethodSelection account={account} />,
        previous: {
          icon: null,
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          label: 'Continue',
          handleNext: () => {
            dispatchOverlayLoad("Transfer investments to T. Rowe Price");
          },
          variant: 'primary',
          icon: <ChevronRight slot="suffix" className="chevron-white" />
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      },
      {
        component: <TransferInstructions account={account} />,
        previous: {
          icon: <ChevronLeft slot="prefix" className="chevron-blue" />,
          label: 'Back',
          handlePrevious: () => {
            dispatchOverlayLoad(taxableSweep ? "Choose your sweep account funding method – Taxable" : "Choose your sweep account funding method – Nontaxable");
          }
        },
        next: {
          label: 'Start My Transfer',
          icon: null,
          handleNext: () => {
            handleSSJNav(FUNDING_METHODS.TRANSFER, account);
          },
          variant: 'primary'
        },
        showStepCount: fundingMethod !== 'DEFAULT' && !isNil(fundingMethod)
      }
    ],
    BROKERAGE_TRADING: [
      {
        component: <BrokerageTradingInstructions />,
        previous: {
          icon: null,
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          disable: webViewEnabled || requestStatus['updateMission'] === 'REQUESTED',
          label: onboardingMaintenanceOptOut
            ? 'Opt Out'
            : 'Start Brokerage Trading',
          handleNext: () => {
            //Cannot opt out when CWV enabled
            //For some reason even if a button is disabled, Beacon is still dispatching the onClick callback
            if (webViewEnabled) {
              return;
            }

            if (onboardingMaintenanceOptOut) {
              const agreementsForMaintenanceOptOut =
                getAgreementsForMaintenanceOptOut(
                  rolledUpMissions,
                  MAINTENANCE_METHODS.BROKERAGE_TRADING
                );
              optOutOfClientMissions(agreementsForMaintenanceOptOut, MAINTENANCE_METHODS.BROKERAGE_TRADING)
                .then(() => {
                  getClientMissions();
                  closeStepModal('step-modal-onboarding');
                })
                .catch(() => {
                  //do nothing
                })
            } else {
              //Should only have one brokerage agreement for a registration
              const brokerageAgreement = rolledUpMissions.find((m) =>
                isBrokerage(m.agreementKey.productType.enumerationID)
              );
              openBrokerageAuthTab(
                brokerageAgreement
                  ? brokerageAgreement.agreementKey.agreementID
                  : undefined,
                'TRADING'
              );
            }
          },
          variant: onboardingMaintenanceOptOut ? 'negative' : 'primary',
          icon: onboardingMaintenanceOptOut ? null : (
            <ExportIcon slot="suffix" className="chevron-blue" />
          )
        },
        showStepCount: false
      }
    ],
    BENEFICIARY: [
      {
        component: <BeneficiaryInstructions />,
        previous: {
          icon: null,
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          label: onboardingMaintenanceOptOut
            ? 'Opt Out'
            : 'Set Up Beneficiaries',
          handleNext: () => {
            if (onboardingMaintenanceOptOut) {
              //Cannot opt out when CWV enabled
              if (webViewEnabled) {
                return;
              }
              const agreementsForMaintenanceOptOut =
                getAgreementsForMaintenanceOptOut(
                  rolledUpMissions,
                  MAINTENANCE_METHODS.BENEFICIARY
                );
              optOutOfClientMissions(agreementsForMaintenanceOptOut, MAINTENANCE_METHODS.BENEFICIARY)
                .then(() => {
                  getClientMissions();
                  closeStepModal('step-modal-onboarding');
                })
                .catch(() => {
                  //do nothing
                })
            } else {
              closeStepModal('step-modal-onboarding');
              history.push('/profile/beneficiaries');
            }
          },
          icon: null,
          disable: (webViewEnabled && onboardingMaintenanceOptOut) || requestStatus['updateMission'] === 'REQUESTED',
          variant: onboardingMaintenanceOptOut ? 'negative' : 'primary'
        },
        showStepCount: false
      }
    ],
    RECURRING_BUY: [
      {
        component: <AutoBuyInstructions />,
        previous: {
          icon: null,
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          label: onboardingMaintenanceOptOut
            ? 'Opt Out'
            : 'Set Up Automatic Buy',
          handleNext: () => {
            if (onboardingMaintenanceOptOut) {
              //Cannot opt out when CWV enabled
              if (webViewEnabled) {
                return;
              }
              const agreementsForMaintenanceOptOut =
                getAgreementsForMaintenanceOptOut(
                  rolledUpMissions,
                  MAINTENANCE_METHODS.RECURRING_BUY
                );
              optOutOfClientMissions(agreementsForMaintenanceOptOut, MAINTENANCE_METHODS.RECURRING_BUY)
                .then(() => {
                  getClientMissions();
                  closeStepModal('step-modal-onboarding');
                })
                .catch(() => {
                  //do nothing
                })
            } else {
              handleSSJNav(MAINTENANCE_METHODS.RECURRING_BUY, account);
            }
          },
          icon: null,
          disable: (webViewEnabled && onboardingMaintenanceOptOut) || requestStatus['updateMission'] === 'REQUESTED',
          variant: onboardingMaintenanceOptOut ? 'negative' : 'primary'
        },
        showStepCount: false
      }
    ],
    TRUSTED_CONTACT: [
      {
        component: <TrustedContactInstructions />,
        previous: {
          label: 'Cancel',
          handlePrevious: () => {}
        },
        next: {
          label: onboardingMaintenanceOptOut
            ? 'Opt Out'
            : 'Add Trusted Contact(s)',
          handleNext: () => {
            if (onboardingMaintenanceOptOut) {
              //Cannot opt out when CWV enabled
              if (webViewEnabled) {
                return;
              }
              const agreementsForMaintenanceOptOut =
                getAgreementsForMaintenanceOptOut(
                  rolledUpMissions,
                  MAINTENANCE_METHODS.TRUSTED_CONTACT
                );

              optOutOfClientMissions(agreementsForMaintenanceOptOut, MAINTENANCE_METHODS.TRUSTED_CONTACT)
                .then(() => {
                  getClientMissions();
                  closeStepModal('step-modal-onboarding');
                })
                .catch(() => {
                  //do nothing
                })
            } else {
              closeStepModal('step-modal-onboarding');
              history.push('/profile/trusted-contacts');
            }
          },
          disable: (webViewEnabled && onboardingMaintenanceOptOut) || requestStatus['updateMission'] === 'REQUESTED',
          variant: onboardingMaintenanceOptOut ? 'negative' : 'primary'
        },
        showStepCount: false
      }
    ],
    MAINTENANCE_OPT_OUT_ERROR: [
      {
        component: <MaintenanceOptOutErrorInstructions />,
        next: null,
        previous: {
          icon: null,
          label: 'Close',
          handlePrevious: () => {}
        },
        showStepCount: false
      }
    ]
  };
};
